import React, { useMemo } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { useAuth } from "@/lib/auth";
import { useBranding, brandingFallback } from "@/lib/branding";
import { Button } from "@/components/ui/button";
import { Flame, Compass } from "lucide-react";

function greetingFor(hour) {
    if (hour < 12) return "Good morning";
    if (hour < 18) return "Good afternoon";
    return "Good evening";
}

/**
 * WelcomeHero — calm, spacious hero card anchoring the student dashboard.
 *
 * Anatomy:
 *  - Oversized serif greeting + first name
 *  - Optional streak chip (consecutive study days this week)
 *  - Browse-catalog CTA
 *  - Institution logo rendered as a quiet watermark on the right; never a
 *    broken image because `useBranding` falls back to the local SVG monogram.
 */
export default function WelcomeHero({ studyStreak = 0 }) {
    const { user } = useAuth();
    const branding = useBranding();
    const greeting = useMemo(() => greetingFor(new Date().getHours()), []);
    const firstName = (user?.name || "").split(" ")[0] || "there";

    return (
        <motion.section
            variants={{
                hidden: { opacity: 0, y: 18 },
                show: {
                    opacity: 1,
                    y: 0,
                    transition: { type: "spring", stiffness: 320, damping: 30 },
                },
            }}
            className="relative overflow-hidden rounded-2xl bg-card border border-primary/10 p-8 sm:p-12"
            data-testid="welcome-hero"
        >
            {/* Watermark logo — always shows, never broken */}
            <img
                src={branding.logo_url}
                alt=""
                aria-hidden
                className="pointer-events-none absolute -right-10 -bottom-12 h-64 w-64 object-contain opacity-[0.05] select-none" onError={brandingFallback}
            />
            {/* Soft gold gradient blob */}
            <div
                aria-hidden
                className="pointer-events-none absolute -top-24 -right-24 h-72 w-72 rounded-full bg-accent/12 blur-3xl"
            />

            <div className="relative z-10 flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6">
                <div>
                    <div className="flex items-center gap-2 text-xs uppercase tracking-[0.22em] text-accent font-semibold">
                        <Compass className="h-3.5 w-3.5" />
                        {branding.institution_name.replace(/ eLearning$/i, "")}
                    </div>
                    <h1
                        className="font-display text-4xl sm:text-5xl lg:text-6xl text-primary tracking-tight mt-3 leading-[1.02]"
                        data-testid="welcome-hero-title"
                    >
                        {greeting}, <span className="text-accent">{firstName.replace(/\.$/, "")}</span>.
                    </h1>
                    <p className="mt-4 text-base text-muted-foreground max-w-xl leading-relaxed">
                        Pick up where you left off, or browse the full catalog. Your
                        learning momentum is yours to own.
                    </p>
                </div>

                <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3 flex-shrink-0">
                    {studyStreak > 0 && (
                        <motion.div
                            initial={{ scale: 0.9, opacity: 0 }}
                            animate={{ scale: 1, opacity: 1 }}
                            transition={{ delay: 0.25, type: "spring", stiffness: 400, damping: 24 }}
                            className="inline-flex items-center gap-2 rounded-full bg-accent/10 ring-1 ring-accent/30 px-4 py-2"
                            data-testid="welcome-hero-streak"
                            title={`${studyStreak}-day study streak`}
                        >
                            <Flame className="h-4 w-4 text-accent" />
                            <span className="text-sm font-semibold text-primary">
                                {studyStreak}-day streak
                            </span>
                        </motion.div>
                    )}
                    <Button
                        asChild
                        variant="outline"
                        className="border-primary/20 hover:bg-primary/5 hover:text-primary"
                        data-testid="browse-catalog-button"
                    >
                        <Link to="/">Browse catalog</Link>
                    </Button>
                </div>
            </div>
        </motion.section>
    );
}
